import {crossedRaceGate} from './raceGeometry.js';
export {raceGateDistance,crossedRaceGate} from './raceGeometry.js';
import { ITEMS } from './rpg.js';
import { mulberry32, rangeInt, range, pick } from './rng.js';
import { BoxColliderField } from './worldgen/boxColliders.js';
import * as pmath from './pmath.js';
import { RACE_WALL_CHAIN_STRIDE, planRaceApproach, type RaceTraversalPlan } from './racePlanner.js';
import type { RaceShutter } from './raceObstacles.js';
import {makeRaceTubePanels,raceBoxCollider,raceBoxExtents,raceTubeFrame,type RaceBasis} from './raceGeometry.js';
import { generateRaceTargets, RACE_TARGET_REFUND, type RaceTarget } from './raceTargets.js';
import type {RaceArena,RaceRhythm} from './raceEncounter.js';
import {addRaceFinale} from './raceFinale.js';
export { simulateRaceTrajectory } from './racePlanner.js';
export type { RaceTraversalPlan, RaceTrajectoryCandidate, RaceTrajectoryResult } from './racePlanner.js';

/** This version changes whenever the course or the validated movement profile changes. */
export const RACE_COURSE_VERSION = 28;
export const RACE_RADIUS = 60_000;
export const RACE_MAX_PLAYERS = 8;
/** Reserve capacity. Fresh attempts carry one cell; subsequent fuel comes from target hits. */
export const RACE_BOOST_BUDGET = 100;
export const RACE_START_ENERGY = 100;
// Plan net refills after a default Longshot shot, retaining enough to fire again.
const PLANNED_SHOT_COST=ITEMS["rifle-longshot"].weapon!.energyCost!;
const NET_SHOT_REFILL=RACE_TARGET_REFUND-PLANNED_SHOT_COST;
// Keep the established geometry search envelope; reconcile shot costs below.
const PLANNED_REFILL=RACE_TARGET_REFUND;
export const RACE_PLANNED_TARGET_LIMIT = 6;
export type RaceVec3 = [number, number, number];
export type RaceDifficulty = 'standard' | 'hard' | 'expert';
export type RaceSectionKind = 'jump' | 'glide' | 'wall' | 'wall-chain' | 'boost' | 'dive';
export type RaceGateRequirement = 'any' | 'air' | 'wall' | 'ground' | 'dive';
export interface RaceFrame { origin: RaceVec3; forward: RaceVec3; right: RaceVec3 }

/** All coordinates are pad-local: controller world position adds radius to Y. */
export interface RaceBox {
  id: string;
  kind: 'platform' | 'wall' | 'tunnel' | 'obstacle' | 'wall-hazard' | 'dive-hazard';
  center: RaceVec3;
  size: RaceVec3;
  standable: boolean;
  /** Large flat decks use their exact top plane instead of radial support. */
  planarSupport?: boolean;
  /** Rotation around world Y, matching a THREE.Object3D rotation.y. */
  yaw: number;
  /** Optional full orthonormal frame for round/curved hull panels. */
  basis?: RaceBasis;
}

/** Ordered planes: negative signed distance before, positive after. */
export interface RaceGate {
  id: string;
  sectionId: string;
  center: RaceVec3;
  halfWidth: number;
  halfHeight: number;
  normal: RaceVec3;
  right: RaceVec3;
  /** Second aperture axis; defaults to world Y for upright gates. */
  up?: RaceVec3;
  requirement: RaceGateRequirement;
  /** Presentation cue only; every generated gate accepts every movement mode. */
  kind?: RaceGateRequirement | 'tunnel';
  shape?: 'round';
  /** Suggested wall side for visual route cues; never a scoring restriction. */
  wallSide?: -1 | 1;
  label: string;
  /** Alternative apertures satisfy this same ordered stage, never an extra gate. */
  alternatives?: RaceGate[];
}

export interface RaceSection {
  id: string;
  kind: RaceSectionKind;
  frame: RaceFrame;
  /** Direction to walk after landing; the next section can turn on this deck. */
  exitForward: RaceVec3;
  planning: RaceTraversalPlan;
  /** Feet position at the recommended jump marker, not a respawn/checkpoint. */
  launch: RaceVec3;
  /** Recommended touchdown position; the unrestricted deck gate is farther along the pad. */
  landing: RaceVec3;
  /** Section-local longitudinal references: takeoff is zero, forward is negative Z. */
  takeoffZ: number;
  landingNearZ: number;
  gap: number;
  drop: number;
  wallSide: -1 | 1;
  /** Ordered wall attachments: local feet X, local jump-off Z, gate index, and solid box ID. */
  wallChain?: { lanes: number[]; jumpZ: number[]; gateIndices: number[]; wallIds: string[] };
  /** Both routes share the first wall jump; the paid route then flies between strips. */
  branch?: { kind: 'flight'; lane: number; planning: RaceTraversalPlan; gateIndices: number[]; rejoinDistance: number };
  /** Positive takeoff-relative bounds; interior gate stages never become respawns. */
  tunnel?: { boxIds: string[]; gateIndices: number[]; startDistance: number; endDistance: number; path:RaceVec3[]; radius:number };
  /** Solid vertical building shaft; all dimensions and heights are pad-local. */
  diveShaft?: { center: RaceVec3; topY: number; bottomY: number; halfWidth: number; halfDepth: number;
    exitCenter: RaceVec3; exitHalfWidth: number; exitHalfHeight: number; gateIndices: number[]; boxIds: string[]; radius:number; path:RaceVec3[] };
  shutterId?: string;
  firstGate: number;
  lastGate: number;
  /** Falling below this height restarts the entire course. */
  failY: number;
}

export interface RaceCourse {
  version: number;
  seed: number;
  difficulty: RaceDifficulty;
  radius: number;
  boxes: RaceBox[];
  gates: RaceGate[];
  spawns: RaceVec3[];
  spawnRotation: [number, number, number, number];
  sections: RaceSection[];
  shutters: RaceShutter[];
  targets: RaceTarget[];
  rhythm?: RaceRhythm;
  arena?: RaceArena;
  bounds: { min: RaceVec3; max: RaceVec3 };
  failY: number;
  length: number;
  planning: { algorithm: 'trajectory-search-v1'; candidates: number; rejected: number; restarts: number; simulatedFuel: number; fuelUpperBound: number; refillTargetIds: string[]; refillEnergy: number; finishReserve: number };
}

export function raceSectionPoint(section: Pick<RaceSection, 'frame'>, local: readonly number[]): RaceVec3 {
  const { origin, forward, right } = section.frame;
  return [origin[0] + right[0] * local[0] - forward[0] * local[2], origin[1] + local[1], origin[2] + right[2] * local[0] - forward[2] * local[2]];
}

export function raceSectionLocal(section: Pick<RaceSection, 'frame'>, point: readonly number[]): RaceVec3 {
  const { origin, forward, right } = section.frame, x = point[0] - origin[0], z = point[2] - origin[2];
  return [x * right[0] + z * right[2], point[1] - origin[1], -x * forward[0] - z * forward[2]];
}


export function raceSpawnQuaternion(course: RaceCourse): [number, number, number, number] {
  const f = course.sections[0].frame.forward, yaw = pmath.atan2(-f[0], -f[2]);
  return [0, pmath.sin(yaw / 2), 0, pmath.cos(yaw / 2)];
}

/** Seed selects a visible difficulty; dimensions then vary within its catalogue. */
export function raceDifficultyForSeed(seed: number): RaceDifficulty {
  return (['standard', 'hard', 'expert'] as const)[(seed >>> 0) % 3];
}

/** Difficulty sets search bounds; accepted distances/heights come from rollouts. */
export const RACE_SECTION_ENVELOPES = {
  standard: { width: [7, 8], depth: [9, 10], jump: [5.5, 7], glide: [45, 54], wall: [55, 62], boost: [110, 128], lane: [2.8, 3.15], fuelFloor: 115 },
  hard: { width: [5.8, 6.5], depth: [8, 9], jump: [6.5, 8], glide: [48, 57], wall: [60, 68], boost: [120, 138], lane: [3.2, 3.6], fuelFloor: 123 },
  expert: { width: [4.5, 5.2], depth: [7, 8], jump: [7, 8.7], glide: [50, 62], wall: [63, 73], boost: [128, 148], lane: [3.6, 4], fuelFloor: 130 },
} as const;

const cm = (value: number) => Math.round(value * 100) / 100;
const START_DECK_DEPTH = 24;
const frameFor = (origin: RaceVec3, yaw: number): RaceFrame => ({ origin, forward: [-pmath.sin(yaw), 0, -pmath.cos(yaw)], right: [pmath.cos(yaw), 0, -pmath.sin(yaw)] });
const distance2 = (a: readonly number[], b: readonly number[]) => pmath.hypot(a[0] - b[0], a[2] - b[2]);
function segmentDistance(point: readonly number[], a: readonly number[], b: readonly number[]): number {
  const dx = b[0] - a[0], dz = b[2] - a[2], length2 = dx * dx + dz * dz;
  const t = Math.max(0, Math.min(1, ((point[0] - a[0]) * dx + (point[2] - a[2]) * dz) / Math.max(.001, length2)));
  return pmath.hypot(point[0] - a[0] - dx * t, point[2] - a[2] - dz * t);
}

/**
 * Bounded constructive search. A seed samples movement schedules, geometry and
 * turns; scalar movement rollouts, reserve accounting and collision corridors
 * reject candidates before they become course geometry. No authored fallback
 * route is emitted if the bounded search fails.
 */
export function generateRaceCourse(seed: number): RaceCourse {
  const courseSeed = seed >>> 0, difficulty = raceDifficultyForSeed(courseSeed), envelope = RACE_SECTION_ENVELOPES[difficulty];
  const rng = mulberry32(courseSeed ^ 0x9b430a21);
  let candidates = 0, rejected = 0;
  // The stronger, shorter launch narrows the expert fuel window. Continue the
  // same stream for rare hard seeds without relaxing feasibility or fuel caps.
  for (let search = 0; search < 256; search++) {
    const boxes: RaceBox[] = [], gates: RaceGate[] = [], sections: RaceSection[] = [], shutters: RaceShutter[] = [];
    const count = rangeInt(rng, 7, 9), initialYaw = cm(range(rng, -Math.PI, Math.PI));
    let yaw = initialYaw, deck: RaceVec3 = [0, 112, 0], deckDepth = START_DECK_DEPTH;
    let simulatedFuel = 0, fuelUpperBound = 0, length = 0, failed = false;
    let plannedReserve = 0, refillEnergy = 0;
    const refillSectionIds = new Set<string>();
    const required = new Set<string>(['jump', 'glide', 'wall', 'wall-chain', 'boost', 'rise', 'dive']);
    boxes.push({ id: 'start', kind: 'platform', center: [0, 111, 0], size: [18, 2, START_DECK_DEPTH], standable: true, yaw });
    for (let index = 0; index < count; index++) {
      // A credited shot is available on this deck before departure. Count each
      // target once; waiting for its eight-second respawn never enters the plan.
      if (plannedReserve <= RACE_BOOST_BUDGET - PLANNED_REFILL && refillSectionIds.size < RACE_PLANNED_TARGET_LIMIT) {
        refillSectionIds.add(`section-${index}`);
        const credit = Math.min(PLANNED_REFILL, RACE_BOOST_BUDGET - plannedReserve);
        plannedReserve += credit; refillEnergy += credit;
      }
      const f = frameFor(deck, yaw);
      const origin: RaceVec3 = [cm(deck[0] + f.forward[0] * deckDepth / 2), deck[1], cm(deck[2] + f.forward[2] * deckDepth / 2)];
      const frame = frameFor(origin, yaw);
      const sectionFrame = { frame };
      const slots = count - index;
      let accepted = false;
      for (let attempt = 0; attempt < 72; attempt++) {
        candidates++;
        const forced = slots <= required.size;
        const token = forced || rng() < .52 ? pick(rng, [...required].length ? [...required] : ['jump', 'glide', 'wall', 'dive']) : pick(rng, ['jump', 'jump', 'glide', 'wall', 'wall-chain', 'dive']);
        const kind = (token === 'rise' ? 'glide' : token) as RaceSectionKind;
        const rising = token === 'rise' || (kind === 'wall' && required.has('rise') && rng() < .3);
        const width = cm(range(rng, envelope.width[0], envelope.width[1])), depth = cm(range(rng, envelope.depth[0], envelope.depth[1]));
        const delta = range(rng, .35, 1.05) * (rng() < .5 ? -1 : 1);
        const exitYaw = cm(yaw + delta), exit = frameFor([0, 0, 0], exitYaw);
        const branching = kind === 'wall-chain' && required.has('wall-chain');
        const wallCount = kind === 'wall-chain' ? rangeInt(rng, 3, branching ? 4 : 5) : 1;
        const lane = cm(range(rng, envelope.lane[0], envelope.lane[1])), wallSide: -1 | 1 = rng() < .5 ? -1 : 1;
        const wallOffsets = [0];
        for (let wall = 1; wall < wallCount; wall++) wallOffsets.push(cm(wallOffsets[wall - 1] + range(rng, RACE_WALL_CHAIN_STRIDE, RACE_WALL_CHAIN_STRIDE + 1.5)));
        const wallLanes = wallOffsets.map((_, wall) => wall % 2 ? -wallSide * cm(lane + range(rng, -.12, .12)) : wall ? cm(range(rng, -.12, .12)) : 0);
        const lastWallOffset = wallOffsets.at(-1)!;
        const shaftDrop = kind === 'dive' ? cm(range(rng, 82, 112)) : 0;
        const gap = cm(rising ? range(rng, 32, 43) : kind === 'wall-chain' ? range(rng, 44, 49) + lastWallOffset : kind === 'dive' ? range(rng, 84, 104) : range(rng, envelope[kind][0], envelope[kind][1]));
        const drop = cm(rising ? -range(rng, .8, 3.2) : kind === 'jump' ? range(rng, 3.5, 5.5) : kind === 'glide' ? range(rng, 10, 16) : kind === 'dive' ? shaftDrop + range(rng, 43, 49) : range(rng, 7, 12));
        // Descending approaches reach the deck before a long, fuel-burning hover.
        const pitch = rising ? cm(range(rng, -.16, -.08)) : kind === 'jump' ? -.13 : kind === 'dive' ? cm(range(rng, .16, .24)) : cm(range(rng, -.13, .17));
        const coastDistance = kind === 'dive' ? 1000 : rising ? 12 : kind === 'boost' ? 48 : 36;
        // Recoverable dive momentum reaches the landing faster; begin the same
        // hover brake sooner so touchdown stays before its grounded gate.
        const brakeDistance = kind === 'dive' ? 20 : rising ? 6 : kind === 'boost' ? 12 : 10;
        const extent = Math.abs(pmath.sin(delta)) * width / 2 + Math.abs(pmath.cos(delta)) * depth / 2;
        const distance = cm(gap + extent);
        const trajectory = { kind, distance, drop, landingRadius: kind === 'jump' ? Math.min(width, depth) / 2 - .7 : 1, wallCount, pitch, coastDistance, brakeDistance, doubleJumpAt: .6, wallDepartureDistance: lastWallOffset + 11.2, ...(kind === 'dive' ? { diveStartDistance: 2, diveAlignDistance: cm(range(rng, 7.4, 8.7)), diveReleaseDrop: shaftDrop } : {}) };
        const rollout = planRaceApproach(trajectory);
        const branchRollout = branching ? planRaceApproach({ ...trajectory, kind: 'wall', wallCount: 1, wallDepartureDistance: 11.2, coastDistance: 28, pitch: cm(range(rng, -.13, .03)) }) : undefined;
        const sectionFuel = Math.max(rollout.plan.fuelUpperBound, branchRollout?.plan.fuelUpperBound ?? 0);
        const stillRequired = new Set(required); stillRequired.delete(token === 'rise' ? 'rise' : kind); if (rising) stillRequired.delete('rise');
        const futureFuel = [...stillRequired].reduce((total, need) => total + (need === 'boost' ? 57 : need === 'rise' ? 12 : need === 'wall-chain' ? 35 : need === 'jump' ? 0 : 12), 0);
        if (!rollout.feasible || (branchRollout && (!branchRollout.feasible || branchRollout.plan.estimatedFuel < rollout.plan.estimatedFuel + 14)) || sectionFuel > plannedReserve - 2 || fuelUpperBound + sectionFuel + futureFuel > refillEnergy + (RACE_PLANNED_TARGET_LIMIT - refillSectionIds.size) * PLANNED_REFILL - 2) { rejected++; continue; }
        const landing = raceSectionPoint(sectionFrame, [0, -drop, -distance]);
        const landingCenter: RaceVec3 = [cm(landing[0]), landing[1], cm(landing[2])];
        const radius = pmath.hypot(width / 2, depth / 2);
        const routeStart = raceSectionPoint(sectionFrame, [0, 0, -2]);
        const lookAhead: RaceVec3 = [landingCenter[0] + exit.forward[0] * 70, landingCenter[1], landingCenter[2] + exit.forward[2] * 70];
        const obstacles = boxes.filter(box => box.id !== (index === 0 ? 'start' : `section-${index - 1}-landing`));
        const obstructed = obstacles.some(box => {
          const extents=raceBoxExtents(box),otherRadius = pmath.hypot(extents[0],extents[2]);
          if (distance2(landingCenter, box.center) < radius + otherRadius + 3) return true;
          if (segmentDistance(box.center, routeStart, landingCenter) < otherRadius + 2) return true;
          return segmentDistance(box.center, landingCenter, lookAhead) < otherRadius + 4;
        });
        if (obstructed) { rejected++; continue; }
        const id = `section-${index}`, firstGate = gates.length;
        const sampleAt = (distance: number, result = rollout) => result.samples.reduce((best, next) => Math.abs(next[0] - distance) < Math.abs(best[0] - distance) ? next : best);
        const addGate = (suffix: string, local: RaceVec3, halfWidth: number, halfHeight: number, requirement: RaceGateRequirement, side?: -1 | 1) => {
          gates.push({ id: `${id}-${suffix}`, sectionId: id, center: raceSectionPoint(sectionFrame, local), halfWidth, halfHeight, normal: [...frame.forward], right: [...frame.right], requirement:'any',kind:requirement, label: String(gates.length + 1).padStart(2, '0'), ...(side === undefined ? {} : { wallSide: side }) });
        };
        let wallChain: RaceSection['wallChain'];
        let branch: RaceSection['branch'];
        let tunnel: RaceSection['tunnel'];
        let diveShaft: RaceSection['diveShaft'];
        let shutterId: string | undefined;
        const branchLane = cm(-wallSide * (lane / 2 + range(rng, -.08, .08)));
        const branchGateIndices: number[] = [];
        const addBranchGate = (distance: number, laneX: number) => {
          const gate = gates.at(-1)!, sample = sampleAt(distance, branchRollout!);
          // One aperture covers both suggested lines. Contact with the wall is
          // optional; fast low flight earns exactly the same ordered stage.
          const local=raceSectionLocal(sectionFrame,gate.center),low=Math.min(local[1]-gate.halfHeight,sample[1]-5),high=Math.max(local[1]+gate.halfHeight,sample[1]+5);
          // Keep the tight wall-side aperture. The paid line only extends its
          // inner edge enough for a capsule; it must not recenter across the gap.
          const left=Math.min(local[0]-gate.halfWidth,laneX-.65),right=Math.max(local[0]+gate.halfWidth,laneX+.65);
          gate.center=raceSectionPoint(sectionFrame,[(left+right)/2,(low+high)/2,-distance]);
          gate.halfHeight=(high-low)/2;gate.halfWidth=(right-left)/2;
          branchGateIndices.push(gates.length - 1);
        };
        if (kind === 'wall' || kind === 'wall-chain') {
          const lanes: number[] = [], jumpZ: number[] = [], gateIndices: number[] = [], wallIds: string[] = [];
          for (let wall = 0; wall < wallCount; wall++) {
            const side: -1 | 1 = (wall % 2 ? -wallSide : wallSide) as -1 | 1;
            const laneX = wallLanes[wall], offset = wallOffsets[wall];
            const near = wall === 0 ? -.5 : 5 - offset, far = -(13 + offset);
            const wallId = `${id}-wall-${wall}`;
            boxes.push({ id: wallId, kind: 'wall', center: raceSectionPoint(sectionFrame, [laneX + side * 1.38, 3, (near + far) / 2]), size: [1, 28, near - far], standable: false, yaw });
            // The last strip is unsafe: leave the wall after its gate, before
            // the red band. Both body clearances retain room for the transfer.
            boxes.push({ id: `${wallId}-hazard`, kind: 'wall-hazard', center: raceSectionPoint(sectionFrame, [laneX + side * .89, 3, -(12.5 + offset)]), size: [.04, 28, 1], standable: false, yaw });
            lanes.push(laneX); jumpZ.push(-(9 + offset)); wallIds.push(wallId); gateIndices.push(gates.length);
            // Faster carry and gentler attached gravity preserve more height
            // through successive jump-offs. Keep the same gate aperture on
            // that rising arc rather than widening its vertical tolerance.
            // A 2.6 m opening hugs the runnable face instead of spanning the
            // corridor. Players can still fly it, but the wall is the easy line.
            addGate(`wall-${wall}`, [laneX-side*.42, 1 + wall * 1.25, -(5 + offset)], 1.3, 7, 'wall', side);
            if (branchRollout && wall > 0) addBranchGate(5 + offset, branchLane);
          }
          if (kind === 'wall-chain') wallChain = { lanes, jumpZ, gateIndices, wallIds };
          const airDistance = lastWallOffset + 23;
          const sample = sampleAt(airDistance);
          addGate('air', [0, sample[1] + (wallCount - 1) * 1.6, -airDistance], 3, 7, 'air');
          if (branchRollout) {
            addBranchGate(airDistance, 0);
            branch = { kind: 'flight', lane: branchLane, planning: branchRollout.plan, gateIndices: branchGateIndices, rejoinDistance: lastWallOffset + 14 };
          }
        } else {
          const airDistance = distance * .5;
          const sample = sampleAt(airDistance);
          addGate('air', [0, sample[1], -airDistance], kind === 'jump' ? 2 : 3, kind === 'jump' ? 4 : 7, 'air');
          if (kind === 'dive') {
            gates.pop();
            const settled = rollout.samples.reduce((best, next) => Math.abs(next[1] + shaftDrop) < Math.abs(best[1] + shaftDrop) ? next : best);
            const centerDistance = settled[0], top = -10, bottom = -shaftDrop - 34;
            const radius = cm(range(rng, 4.8, 5.3)), halfWidth=radius, halfDepth=radius;
            const exitTop = -shaftDrop + 4, exitBottom = bottom;
            const path=[top,exitTop,bottom].map(y=>raceSectionPoint(sectionFrame,[0,y,-centerDistance]));
            const panels=makeRaceTubePanels(`${id}-shaft`,'dive-hazard',path,radius,frame.right,
              (segment,radial)=>segment===1&&radial[0]*frame.forward[0]+radial[2]*frame.forward[2]>.55);
            boxes.push(...panels);
            const boxIds=panels.map(panel=>panel.id),gateIndices:number[]=[];
            const gateCount = rangeInt(rng, 3, 5);
            for (let gateIndex = 0; gateIndex < gateCount; gateIndex++) {
              const height = top - 12 + (-shaftDrop + 7 - (top - 12)) * gateIndex / (gateCount - 1);
              gateIndices.push(gates.length);
              addGate(`dive-${gateIndex}`, [0, height, -centerDistance], radius-.6, radius-.6, 'dive');
              const gate = gates.at(-1)!;gate.normal = [0, -1, 0];gate.up = [...frame.forward];gate.shape='round';
            }
            diveShaft = {center:raceSectionPoint(sectionFrame,[0,(top+bottom)/2,-centerDistance]),topY:origin[1]+top,bottomY:origin[1]+bottom,halfWidth,halfDepth,radius,path,
              exitCenter:raceSectionPoint(sectionFrame,[0,(exitTop+exitBottom)/2,-centerDistance-radius]),exitHalfWidth:radius*.8,exitHalfHeight:(exitTop-exitBottom)/2,gateIndices,boxIds};
            const recoveryDistance = centerDistance + (distance - brakeDistance - centerDistance) * .72;
            addGate('recover', [0, sampleAt(recoveryDistance)[1], -recoveryDistance], 3.5, 7, 'air');
          }
          if (kind === 'boost' && !sections.some(section => section.tunnel)) {
            const start = cm(range(rng, 10, 14)), end = cm(Math.min(distance - 14, start + range(rng, 32, 65)));
            const segments = Math.ceil((end - start) / 2),radius=cm(4.25+(width-4.5)*.12);
            const bend=cm(Math.min(3.8,(end-start)*.065))*(rng()<.5?-1:1);
            const pointAt=(u:number):RaceVec3=>{
              const along=start+(end-start)*u;
              const hi=rollout.samples.findIndex(p=>p[0]>=along),b=rollout.samples[Math.max(0,hi)],a=rollout.samples[Math.max(0,hi-1)];
              const t=Math.max(0,Math.min(1,(along-a[0])/Math.max(.001,b[0]-a[0])));
              return raceSectionPoint(sectionFrame,[bend*pmath.sin(pmath.TWO_PI*u)*pmath.sin(pmath.TWO_PI*u/2),
                a[1]+(b[1]-a[1])*t+1+.6*pmath.sin(pmath.TWO_PI*u/2)*pmath.sin(pmath.TWO_PI*u/2),-along]);
            };
            const path=Array.from({length:segments+1},(_,i)=>pointAt(i/segments));
            const panels=makeRaceTubePanels(`${id}-tunnel`,'tunnel',path,radius,frame.right);boxes.push(...panels);
            gates.pop();
            const gateIndices: number[] = [], gateCount = rangeInt(rng, 3, 5);
            for (let checkpoint = 0; checkpoint < gateCount; checkpoint++) {
              const u=(3+(end-start-6)*checkpoint/(gateCount-1))/(end-start);
              const basis=raceTubeFrame(pointAt(Math.max(0,u-.005)),pointAt(Math.min(1,u+.005)),frame.right);
              gateIndices.push(gates.length);
              addGate(`tunnel-${checkpoint}`,[0,0,0],radius-.15,radius-.15,'air');
              Object.assign(gates.at(-1)!,{center:pointAt(u),normal:basis.forward,right:basis.right,up:basis.up,shape:'round',kind:'tunnel'});
            }
            tunnel = { boxIds:panels.map(p=>p.id),gateIndices,startDistance:start,endDistance:end,path,radius };
            const shutterDistance = start + (end - start) * .6, shutterSample = sampleAt(shutterDistance);
            const shutterFrame=raceTubeFrame(pointAt(.595),pointAt(.605),frame.right);
            shutterId = `${id}-shutter`;
            shutters.push({ id: shutterId, sectionId: id, center: pointAt(.6), size: [radius*2, radius*2, .5],
              basis:[shutterFrame.right,shutterFrame.up,shutterFrame.forward.map(v=>-v) as RaceVec3],yaw,
              period: cm(range(rng, 7, 8)), openSeconds: 4.5, transitionSeconds: .5, phase: cm(range(rng, 0, 7)), crossingSeconds: rollout.sampleTimes[rollout.samples.indexOf(shutterSample)] });
          }
        }
        boxes.push({ id: `${id}-landing`, kind: 'platform', center: [landingCenter[0], landingCenter[1] - 1, landingCenter[2]], size: [width, 2, depth], standable: true, yaw: exitYaw });
        // Leave a full input/pose interval before even the earliest next jump.
        const groundGateOffset = Math.min(1, depth / 2 - 2.75);
        gates.push({ id: `${id}-land`, sectionId: id, center: [landingCenter[0] + exit.forward[0] * groundGateOffset, landingCenter[1] + .8, landingCenter[2] + exit.forward[2] * groundGateOffset], normal: [...exit.forward], right: [...exit.right], halfWidth: width / 2, halfHeight: 1.6, requirement: 'any',kind:'ground', label: index === count - 1 ? 'FINISH' : String(gates.length + 1).padStart(2, '0') });
        sections.push({ id, kind, frame, exitForward: [...exit.forward], planning: rollout.plan, launch: raceSectionPoint(sectionFrame, [0, 0, kind === 'jump' ? 1.2 : .8]), landing, takeoffZ: 0, landingNearZ: -gap, gap, drop, wallSide, ...(wallChain ? { wallChain } : {}), ...(branch ? { branch } : {}), ...(tunnel ? { tunnel } : {}), ...(diveShaft ? { diveShaft } : {}), ...(shutterId ? { shutterId } : {}), firstGate, lastGate: gates.length - 1, failY: landingCenter[1] - 12 });
        required.clear(); for (const need of stillRequired) required.add(need);
        simulatedFuel += rollout.plan.estimatedFuel; fuelUpperBound += sectionFuel; plannedReserve -= sectionFuel;
        length += distance + deckDepth / 2;
        deck = landingCenter; deckDepth = depth; yaw = exitYaw;
        accepted = true; break;
      }
      if (!accepted) { failed = true; break; }
    }
    if (failed || required.size || fuelUpperBound < envelope.fuelFloor || !sections.some(section => section.branch) || !shutters.length) continue;
    const targets = generateRaceTargets({ seed: courseSeed, sections, boxes, radius: RACE_RADIUS, shutters }, refillSectionIds);
    if ([...refillSectionIds].some(id => !targets.some(target => target.sectionId === id && target.plannedRefill))) { rejected++; continue; }
    // Price the reference Longshot on the existing route. Skip refills while
    // nearly full; every credited target must be affordable before it is hit.
    let reserve=RACE_START_ENERGY,netRefills=0,affordable=true;
    for(const section of sections){
      const target=targets.find(t=>t.sectionId===section.id&&t.plannedRefill);
      if(target){
        if(reserve>RACE_BOOST_BUDGET-NET_SHOT_REFILL)target.plannedRefill=false;
        else if(reserve<PLANNED_SHOT_COST){affordable=false;break;}
        else {const after=Math.min(RACE_BOOST_BUDGET,reserve+NET_SHOT_REFILL);netRefills+=after-reserve;reserve=after;}
      }
      reserve-=Math.max(section.planning.fuelUpperBound,section.branch?.planning.fuelUpperBound??0);
      if(reserve<2){affordable=false;break;}
    }
    if(!affordable){rejected++;continue;}

    const startFrame = { frame: frameFor([0, 112, 0], initialYaw) };
    const spawns: RaceVec3[] = [];
    // Four-metre spacing keeps all eight pilots inside the shorter runway,
    // with 15/19m to align and accelerate before the takeoff edge.
    for (let row = 0; row < 2; row++) for (let col = 0; col < 4; col++) spawns.push(raceSectionPoint(startFrame, [(col - 1.5) * 4, 0, 3 + row * 4]));
    const min: RaceVec3 = [Infinity, Infinity, Infinity], max: RaceVec3 = [-Infinity, -Infinity, -Infinity];
    for (const box of boxes) {
      const extents=raceBoxExtents(box);
      for (let axis = 0; axis < 3; axis++) { min[axis] = Math.min(min[axis], box.center[axis] - extents[axis] - 25); max[axis] = Math.max(max[axis], box.center[axis] + extents[axis] + 25); }
    }
    const spawnRotation: [number, number, number, number] = [0, pmath.sin(initialYaw / 2), 0, pmath.cos(initialYaw / 2)];
    return addRaceFinale({ version: RACE_COURSE_VERSION, seed: courseSeed, difficulty, radius: RACE_RADIUS, boxes, gates, spawns, spawnRotation, sections, shutters, targets, bounds: { min, max }, failY: Math.min(...sections.map(section => section.failY)), length: cm(length), planning: { algorithm: 'trajectory-search-v1', candidates, rejected, restarts: search, simulatedFuel: cm(simulatedFuel), fuelUpperBound: cm(fuelUpperBound), refillTargetIds: targets.filter(target => target.plannedRefill).map(target => target.id), refillEnergy: cm(netRefills), finishReserve: cm(reserve) } });
  }
  throw new Error(`Race trajectory search exhausted for seed ${courseSeed}`);
}

/** Static geometry only, with the same exact dimensions used by rendering.
 * Live races must use RaceCourseColliders.update(roundElapsedSeconds) from
 * raceObstacles.ts so moving shutter leaves participate in collision too. */
export function makeRaceCourseColliders(course: RaceCourse): BoxColliderField {
  const field = new BoxColliderField(course.radius);
  for (const box of course.boxes) field.addBox(raceBoxCollider(box,course.radius));
  return field;
}

/** Route labels describe suggested techniques, never enforce a movement mode. */
export function raceGateRequirementMet(_gate: RaceGate, _grounded: boolean, _wallRun: number, _diving = false): boolean {
  return true;
}

/** Match a crossing and its movement requirement on the same alternative. */
export function passedRaceGate(gate: RaceGate, from: readonly number[], to: readonly number[], grounded: boolean, wallRun: number, diving = false): boolean {
  return [gate, ...(gate.alternatives ?? [])].some(aperture => crossedRaceGate(aperture, from, to) && raceGateRequirementMet(aperture, grounded, wallRun, diving));
}

/** Extra recovery airspace beyond the geometry bounds, not an invisible pad wall. */
export const RACE_AIRSPACE_MARGIN = 120;

/** Gate-only lookup remains useful for UI; live deaths also use physical location. */
export function raceFallY(course: RaceCourse, nextGate: number, position?: readonly number[]): number {
  const sectionId = course.gates[nextGate]?.sectionId;
  const activeFloor = course.sections.find(section => section.id === sectionId)?.failY ?? course.failY;
  if (!position) return activeFloor;
  // Gate reports can lag, and a missed gate must not kill someone standing on a
  // later, lower deck. Use nearby physical route geometry, independent of score.
  let floor = Infinity;
  for (const section of course.sections) {
    if (segmentDistance(position, section.frame.origin, section.landing) <= 24) floor = Math.min(floor, section.failY);
  }
  for (const box of course.boxes) if (box.kind === 'platform') {
    const c = pmath.cos(box.yaw), s = pmath.sin(box.yaw), x = position[0] - box.center[0], z = position[2] - box.center[2];
    if (Math.abs(x * c - z * s) <= box.size[0] / 2 + 8 && Math.abs(x * s + z * c) <= box.size[2] / 2 + 8) floor = Math.min(floor, box.center[1] + box.size[1] / 2 - 12);
  }
  return Number.isFinite(floor) ? floor : course.failY;
}

export function isRaceFall(course: RaceCourse, position: readonly number[], nextGate = 0): boolean {
  if (position.length !== 3 || !position.every(Number.isFinite)) return true;
  return position[1] < raceFallY(course, nextGate, position)
    || position[0] < course.bounds.min[0] - RACE_AIRSPACE_MARGIN || position[0] > course.bounds.max[0] + RACE_AIRSPACE_MARGIN
    || position[2] < course.bounds.min[2] - RACE_AIRSPACE_MARGIN || position[2] > course.bounds.max[2] + RACE_AIRSPACE_MARGIN;
}
